/**
 * AnswerDisplay Component
 *
 * Purpose: Display "jumped out" answers above the chat interface.
 * Shows completed answers that have been extracted from the chat.
 *
 * Features:
 * - Single value or array display (pills for arrays)
 * - Click to edit/remove functionality
 * - Visual feedback on hover
 * - Max visible items with overflow indicator
 * - Smooth slide-in animation when appearing
 */

import { animated, useSpring } from "@react-spring/web";
import type React from "react";

interface AnswerDisplayProps {
  label: string;
  value: string | string[];
  onEdit?: () => void;
  onRemoveItem?: (item: string) => void;
  editable?: boolean;
  maxVisible?: number;
}

export const AnswerDisplay: React.FC<AnswerDisplayProps> = ({
  label,
  value,
  onEdit,
  onRemoveItem,
  editable = false,
  maxVisible = 6,
}) => {
  const isArray = Array.isArray(value);
  const displayValue = isArray ? value : [value];
  const visibleItems = displayValue.slice(0, maxVisible);
  const overflowCount = displayValue.length - maxVisible;

  // Slide-in animation from top
  const slideIn = useSpring({
    from: { opacity: 0, transform: "translateY(-20px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { tension: 280, friction: 60 },
  });

  return (
    <animated.div
      style={slideIn}
      className={`
        p-3 rounded-lg transition-all duration-200
        ${editable ? "cursor-pointer hover:bg-white hover:bg-opacity-10" : ""}
      `}
      onClick={editable && !isArray ? onEdit : undefined}
    >
      {/* Label */}
      <div className="text-xs font-medium text-white opacity-75 mb-2">{label}</div>

      {/* Single Value Display */}
      {!isArray && <div className="text-white font-medium">{value}</div>}

      {/* Array Value Display (Pills) */}
      {isArray && (
        <div className="space-y-2">
          {visibleItems.map((item) => (
            <div
              key={item}
              className="group flex items-center justify-between px-3 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors cursor-pointer"
              role={editable && onRemoveItem ? "button" : undefined}
              tabIndex={editable && onRemoveItem ? 0 : undefined}
              onClick={editable && onRemoveItem ? () => onRemoveItem(item) : undefined}
              onKeyDown={editable && onRemoveItem ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRemoveItem(item); } } : undefined}
            >
              <span className="text-white text-sm">{item}</span>
              {editable && onRemoveItem && (
                <span className="opacity-0 group-hover:opacity-100 text-white text-xs transition-opacity" aria-hidden="true">
                  ×
                </span>
              )}
            </div>
          ))}

          {/* Overflow Indicator */}
          {overflowCount > 0 && (
            <div className="text-white text-sm opacity-75 px-3">+{overflowCount} more...</div>
          )}
        </div>
      )}
    </animated.div>
  );
};
